import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import cloudIcon from "../../../assets/images/logo.png";
import { useAuth } from "../../hooks/useAuth";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { resetPassword, loading } = useAuth();
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);

  const { email, otp } = location.state || {};

  if (!email || !otp) {
    navigate('/login');
    return null;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    const result = await resetPassword(email, otp, newPassword);

    if (result.success) {
      setShowSuccess(true);
      setTimeout(() => {
        navigate('/login', { state: { resetSuccess: true } });
      }, 1500);
    } else {
      setError(result.message || "Failed to reset password");
    }
  };

  return (
    <div className="flex h-screen bg-bg-main">
      {/* Left Side - Form */}
      <div className="flex-1 flex flex-col items-center justify-center relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute w-1 h-1 bg-primary-cyan/20 rounded-full animate-float"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 5}s`,
                animationDuration: `${3 + Math.random() * 5}s`,
              }}
            />
          ))}
        </div>

        <div className="absolute top-20 right-20 w-64 h-64 bg-primary-cyan/5 rounded-full blur-3xl animate-pulse-slow"></div>
        <div className="absolute bottom-20 left-20 w-80 h-80 bg-primary-cyan/5 rounded-full blur-3xl animate-pulse-slow animation-delay-1000"></div>

        {/* Logo */}
        <div className="relative z-10 mb-8 animate-fade-in-up">
          <div className="flex items-center justify-center gap-3 mb-4">
            <img src={cloudIcon} alt="Cloud Icon" className="w-16 h-16 animate-float" />
            <div className="text-white text-4xl font-bold leading-tight">
              Cloud<span className="text-primary-cyan">SaaS</span>
            </div>
          </div>
        </div>

        {/* Reset Password Card */}
        <div className="relative z-10 w-[450px] animate-fade-in-up animation-delay-200">
          <div className="bg-card-bg rounded-2xl shadow-2xl p-10 border border-border-color">
            <h2 className="text-3xl font-bold text-white mb-2 tracking-tight text-center">
              Reset Password
            </h2>
            <p className="text-gray-400 text-sm mb-8 text-center">
              Enter your new password for <br />
              <span className="text-primary-cyan font-medium">{email}</span>
            </p>

            {showSuccess ? (
              <div className="bg-green-500/20 border border-green-500 rounded-xl p-4 mb-6">
                <div className="flex items-center justify-center gap-2">
                  <svg className="w-5 h-5 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <p className="text-green-400 text-center">
                    Password reset successfully! Redirecting to login...
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit}>
                {error && (
                  <div className="bg-red-500/20 border border-red-500 rounded-xl p-3 mb-6">
                    <p className="text-red-400 text-sm text-center">{error}</p>
                  </div>
                )}

                <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                  NEW PASSWORD
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-bg-main border border-border-color rounded-xl px-4 py-3 text-white text-sm mb-5 focus:border-primary-cyan focus:outline-none focus:ring-1 focus:ring-primary-cyan transition-all duration-200"
                  placeholder="Enter new password (min 6 characters)"
                  disabled={loading}
                />

                <label className="block text-gray-300 text-xs font-semibold uppercase tracking-wider mb-2">
                  CONFIRM PASSWORD
                </label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-bg-main border border-border-color rounded-xl px-4 py-3 text-white text-sm mb-8 focus:border-primary-cyan focus:outline-none focus:ring-1 focus:ring-primary-cyan transition-all duration-200"
                  placeholder="Confirm your new password"
                  disabled={loading}
                />

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary-cyan text-primary-dark font-semibold py-3 rounded-xl mb-4 hover:opacity-90 hover:shadow-lg transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Resetting..." : "Reset Password"}
                </button>

                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="w-full bg-transparent border border-border-color text-gray-300 font-semibold py-3 rounded-xl hover:border-primary-cyan hover:text-white transition-all duration-200"
                >
                  ← Back to Login
                </button>
              </form>
            )}
          </div>
        </div>
      </div>

      {/* Right Side - Info Section */}
      <div className="flex-1 bg-primary-dark flex items-center justify-center relative overflow-hidden">
        <div className="text-center px-12 relative z-10">
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => (
              <div
                key={i}
                className="absolute w-1 h-1 bg-primary-cyan/20 rounded-full animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 5}s`,
                }}
              />
            ))}
          </div>

          <div className="relative z-10">
            <h1 className="text-6xl font-bold text-white leading-tight mb-4">
              Create a
              <span className="text-primary-cyan"> Strong </span>
              Password
            </h1>
            <div className="w-20 h-1 bg-primary-cyan mx-auto mb-6 rounded-full"></div>
            <p className="text-gray-300 text-lg leading-relaxed max-w-md">
              Your new password must be at least 6 characters long and different from your previous passwords.
            </p>

            <div className="mt-12 grid grid-cols-1 gap-4 text-left">
              <div className="flex items-center gap-3 text-gray-300">
                <svg className="w-5 h-5 text-primary-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span>Minimum 6 characters</span>
              </div>
              <div className="flex items-center gap-3 text-gray-300">
                <svg className="w-5 h-5 text-primary-cyan" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Secure encryption</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;